/**
 * Cloud Functions composable
 * Provides methods for calling Firebase Cloud Functions
 */
import { ref } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseFunctions } from '@/core/firebase/firebase'

/**
 * Composable for calling Cloud Functions
 * @returns {Object} Cloud function methods and state
 */
export function useCloudFunctions() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * Call a Cloud Function
   * @param {string} functionName - Name of the cloud function
   * @param {Object} data - Data to pass to the function
   * @returns {Promise<any>} Function result
   */
  async function callFunction(functionName, data = {}) {
    loading.value = true
    error.value = null

    try {
      const functions = getFirebaseFunctions()
      const callable = httpsCallable(functions, functionName)
      const result = await callable(data)
      return result.data
    } catch (err) {
      console.error(`Cloud function ${functionName} error:`, err)
      error.value = err.message || 'Function call failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  // ============================================
  // Goals Functions
  // ============================================

  /**
   * Get agent sales from Priority ERP
   * @param {Object} params - Request parameters
   * @param {string} params.date_start - Start date (YYYY-MM-DD)
   * @param {string} params.agent_number - Agent number
   * @param {Array<string>} params.makats - Array of product makats to filter
   * @returns {Promise<{sales: Array, missing_clients: Array}>}
   */
  async function getAgentSales({ date_start, agent_number, makats }) {
    return callFunction('goalsGetAgentSales', { date_start, agent_number, makats })
  }

  /**
   * Get total sales amount for a specific client
   * @param {Object} params - Request parameters
   * @param {string} params.date_start - Start date (YYYY-MM-DD)
   * @param {string} params.agent_number - Agent number
   * @param {string} params.client - Client makat
   * @returns {Promise<{amount: number}>}
   */
  async function getAgentSalesForClient({ date_start, agent_number, client }) {
    return callFunction('goalsGetAgentSalesForClient', { date_start, agent_number, client })
  }

  return {
    // State
    loading,
    error,

    // Generic method
    callFunction,

    // Goals specific methods
    getAgentSales,
    getAgentSalesForClient,
  }
}
